import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
	CheckIcon,
	ChevronDownIcon,
	SlidersHorizontalIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

type MultiSelectProps = {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	children: React.ReactNode;
};

const MultiSelect = ({ open, onOpenChange, children }: MultiSelectProps) => {
	return (
		<Popover open={open} onOpenChange={onOpenChange}>
			{children}
		</Popover>
	);
};

type MultiSelectTriggerProps = {
	name: string;
	selectedValues: Set<string>;
	options: Array<{ value: string; label: string }>;
	icon?: React.ReactNode;
	className?: string;
};

const MultiSelectTrigger = ({
	selectedValues,
	options,
	name,
	icon = <SlidersHorizontalIcon />,
	className,
}: MultiSelectTriggerProps) => (
	<PopoverTrigger asChild>
		<Button variant="outline" className={cn(className)}>
			{icon}
			{name}
			{selectedValues.size === 1 && (
				<>
					<Separator orientation="vertical" className="mx-2 h-4" />
					{options
						.filter((option) => selectedValues.has(option.value))
						.map((option) => (
							<Badge
								variant="secondary"
								key={option.value}
								className="rounded-sm px-1 font-normal"
							>
								{option.label}
							</Badge>
						))}
				</>
			)}
			{selectedValues.size > 1 && (
				<>
					<Separator orientation="vertical" className="mx-2 h-4" />
					<Badge variant="secondary" className="rounded-sm px-1 font-normal">
						{selectedValues.size} valgt
					</Badge>
				</>
			)}
			<ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
		</Button>
	</PopoverTrigger>
);

type MultiSelectContentProps = {
	children: React.ReactNode;
	className?: string;
	selectedSize: number;
	clearSelected: () => void;
	searchPlaceholder?: string;
};

const MultiSelectContent = ({
	children,
	className,
	selectedSize,
	clearSelected,
	searchPlaceholder,
}: MultiSelectContentProps) => {
	const { t } = useTranslation();

	return (
		<PopoverContent className={cn("p-0 w-fit", className)}>
			<Command className="w-fit">
				<CommandInput
					className="border-none outline-none focus-visible:ring-0"
					placeholder={searchPlaceholder || t("admin.filter.searchPlaceholder")}
				/>
				<CommandList>
					<CommandEmpty>{t("admin.filter.noResults")}</CommandEmpty>
					<CommandGroup>{children}</CommandGroup>
					{selectedSize > 0 && (
						<>
							<CommandSeparator />
							<CommandGroup>
								<CommandItem
									onSelect={clearSelected}
									className="justify-center text-center text-red-500 font-bold leading-relaxed"
								>
									{t("admin.filter.remove")}
								</CommandItem>
							</CommandGroup>
						</>
					)}
				</CommandList>
			</Command>
		</PopoverContent>
	);
};

type MultiSelectItemProps = {
	option: { value: string; label: string };
	isSelected: boolean;
	toggleValue: (value: string) => void;
};

const MultiSelectItem = ({
	option,
	isSelected,
	toggleValue,
}: MultiSelectItemProps) => (
	<CommandItem key={option.value} onSelect={() => toggleValue(option.value)}>
		<div
			className={cn(
				"mr-2 flex h-4 w-4 items-center justify-center dark:border-gray-400 rounded-sm border border-gray-600",
				isSelected
					? "bg-gray-900 dark:bg-gray-100 text-primary-foreground"
					: "opacity-50 [&_svg]:invisible"
			)}
		>
			<CheckIcon className="h-4 w-4" />
		</div>
		<span>{option.label}</span>
	</CommandItem>
);

export { MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectTrigger };
